import { BookkeepingTagNavItemValue } from '@components'

import { useCallback } from 'react'

import { useAccounts } from '@modules-deprecated/app/accounts'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useTaxRates } from '@modules-deprecated/app/taxrates'

import { BillFormFieldsChangeSource } from '../../../../../enums/billFormFieldsChangeSource'
import { useFormContext } from '../../../../../hooks/useForm'
import { useBillForm } from '../contexts/billFormContext'
import { autoCompleteBillForm } from '../utils/autoCompleteBillForm'
import { BillFormSchema } from '../utils/formData'
import { getBillFormFromBookkeepingTag } from '../utils/getBillFormFromBookkeepingTag'
import { useTrackAutocompletedFields } from '../utils/trackAutocompletedFields'

export const useBookkeepingHeroAutoComplete = () => {
  const { organization } = useUserOrganization()
  const { getValues, reset: resetForm, setValue } = useFormContext<BillFormSchema>()
  const { accounts = [] } = useAccounts()
  const { taxRates = [] } = useTaxRates()
  const { trackAutocompletedFields } = useTrackAutocompletedFields()
  const { billFormView, setFormFieldsChangeSource, formFieldsChangeSource } = useBillForm()

  const autoCompleteWithBookkeepingHeroData = useCallback(
    (
      bookkeepingTagValue: BookkeepingTagNavItemValue | undefined,
      lineIndex: number,
      source: BillFormFieldsChangeSource,
    ) => {
      const billForm = getValues()

      const billFormBookkeepingTag = getBillFormFromBookkeepingTag({
        accounts,
        bookkeepingTagValue,
        existingBillLines: billForm.billLines,
        hasVATdetected: billForm.billLines?.[lineIndex]?.hasVATdetected,
        lineIndex,
        linesTotal: billForm.billLines?.length,
        organizationCountryCode: organization?.countryId || 'DK',
        taxRates,
        vendor: billForm.vendor,
      })

      const billFormUpdated = autoCompleteBillForm({
        billFormDefault: billForm,
        billFormOverrides: billFormBookkeepingTag,
        baseCurrencyId: organization?.baseCurrencyId,
      })

      // bookkeeping tag having the highest priority = remove defaults when removing tag
      resetForm(billFormUpdated, { keepDefaultValues: !!bookkeepingTagValue })

      trackAutocompletedFields({
        billLines: billForm?.billLines?.[lineIndex],
        billFormView,
        context: source,
        formFieldsChangeSource,
        setFormFieldsChangeSource,
        updatedBillLines: billFormUpdated?.billLines?.[lineIndex],
      })

      // Wait one frame before setting autoCompletedFields
      window.requestAnimationFrame(() => {
        setValue('autoCompletedFields', billFormUpdated.autoCompletedFields)
      })
    },
    [
      accounts,
      billFormView,
      formFieldsChangeSource,
      getValues,
      organization?.countryId,
      organization?.baseCurrencyId,
      resetForm,
      setFormFieldsChangeSource,
      setValue,
      taxRates,
      trackAutocompletedFields,
    ],
  )

  return { autoCompleteWithBookkeepingHeroData }
}
